/**
 * Where a vehicle physically is, and what that makes its status — the single
 * source of truth for the location layer of the fleet.
 *
 * The unit owns every vehicle in the pool; what changes from day to day is who
 * is *holding* it. `Vehicle.company` stays the owning company (פלוגת שינוע 99)
 * and never moves; `Vehicle.assignedCompany` is the unit the vehicle is with
 * right now, and it is the only field a loan or a return writes.
 *
 * The badge the screens paint is derived, never stored — one function, three
 * outcomes:
 *
 * - at home (or nothing recorded) → 🟢 `זמין בשינוע`
 * - held by another company        → 🟠 `מוקצה לפלוגת <שם הפלוגה>`
 * - carrying a fault flag          → 🔴 `בטיפול / תקול`
 *
 * The fault flag wins, because a vehicle standing broken in a company yard is
 * not available to that company either. That is also why the two transfer
 * actions below build their patch here rather than in the store: a move must
 * put the operational status back in step with the location, and it must never
 * quietly clear a fault nobody has repaired.
 */
import type { VehicleStatus } from '../types/fleet';

/** The owner unit, as the transfer actions write it. */
export const HOME_COMPANY = 'שינוע';

/** The same company written in full, as the vehicle records carry it. */
export const HOME_COMPANY_FULL = 'פלוגת שינוע 99';

/**
 * Every spelling of the home company an operator (or an older record) might
 * have left behind. A blank is home too — nothing recorded means the vehicle
 * never left.
 */
const HOME_ALIASES: ReadonlySet<string> = new Set([
  '',
  HOME_COMPANY,
  HOME_COMPANY_FULL,
  'שינוע 99',
  'פלוגת שינוע',
]);

/**
 * The companies the unit actually lends to, offered by the transfer dialog.
 * The list is a convenience, not a constraint: the dialog also takes a name
 * typed by hand, so a company that is not on it can still hold a vehicle.
 */
export const EXTERNAL_COMPANIES: readonly string[] = [
  'פלק"ד',
  'מ.צ',
  'סמג"ד',
  'חש"ל',
  'מג"ד',
];

/** The three states a vehicle's location can read as. */
export const VEHICLE_LOCATION_KINDS = ['home', 'loaned', 'maintenance'] as const;

export type VehicleLocationKind = (typeof VEHICLE_LOCATION_KINDS)[number];

/** Traffic-light tone the badge paints with, one per kind. */
export const VEHICLE_LOCATION_TONES: Record<VehicleLocationKind, 'green' | 'amber' | 'red'> = {
  home: 'green',
  loaned: 'amber',
  maintenance: 'red',
};

/** Tailwind classes for the badge, keyed the same way. */
export const VEHICLE_LOCATION_STYLES: Record<VehicleLocationKind, { dot: string; chip: string }> = {
  home: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  loaned: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200' },
  maintenance: { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/**
 * The glyph the badge leads with. The compact roster row is read at a glance
 * and its badge is one short line, so the state has to survive being skimmed —
 * 🟢 זמין בשינוע, 🟠 מוקצה לפלוגת פלק"ד, 🔴 בטיפול / תקול.
 */
export const VEHICLE_LOCATION_ICONS: Record<VehicleLocationKind, string> = {
  home: '🟢',
  loaned: '🟠',
  maintenance: '🔴',
};

/** The sentence a maintenance vehicle reads as, wherever it stands. */
export const MAINTENANCE_LABEL = 'בטיפול / תקול';

/** The sentence a vehicle at base reads as. */
export const HOME_LABEL = 'זמין בשינוע';

/** The statuses that mean the vehicle is not roadworthy. */
const FAULT_STATUSES: ReadonlySet<VehicleStatus> = new Set<VehicleStatus>([
  'maintenance',
  'grounded',
]);

export interface VehicleLocation {
  kind: VehicleLocationKind;
  /** The unit holding the vehicle — the home company when it is at base. */
  company: string;
  /** Ready for the screen, e.g. `מוקצה לפלוגת פלק"ד`. */
  label: string;
  tone: 'green' | 'amber' | 'red';
}

/** The part of a vehicle the derivation reads — any stored record satisfies it. */
export interface VehicleLocationProbe {
  assignedCompany?: string | null;
  status?: VehicleStatus;
}

/** Trims a typed or stored company name; `null`/`undefined` read as blank. */
export const normalizeCompany = (raw: string | null | undefined): string =>
  String(raw ?? '').trim();

/** True when the name means "with us" — including nothing recorded at all. */
export function isHomeCompany(raw: string | null | undefined): boolean {
  return HOME_ALIASES.has(normalizeCompany(raw));
}

/** True when the status says the vehicle cannot go out, wherever it stands. */
export function isFaultStatus(status: VehicleStatus | null | undefined): boolean {
  return status !== null && status !== undefined && FAULT_STATUSES.has(status);
}

/** `מוקצה לפלוגת פלק"ד` — the sentence a loaned vehicle reads as. */
export const loanedLabel = (company: string): string => `מוקצה לפלוגת ${company}`;

/**
 * The badge for one vehicle. Pure, and the only place the three outcomes are
 * decided — the card, the details modal, the filters and the healthcheck all
 * read it rather than re-deciding.
 */
export function describeVehicleLocation(vehicle: VehicleLocationProbe): VehicleLocation {
  const company = normalizeCompany(vehicle.assignedCompany);
  if (isFaultStatus(vehicle.status)) {
    return {
      kind: 'maintenance',
      company: company || HOME_COMPANY,
      label: MAINTENANCE_LABEL,
      tone: VEHICLE_LOCATION_TONES.maintenance,
    };
  }
  if (isHomeCompany(company)) {
    return {
      kind: 'home',
      company: HOME_COMPANY,
      label: HOME_LABEL,
      tone: VEHICLE_LOCATION_TONES.home,
    };
  }
  return {
    kind: 'loaned',
    company,
    label: loanedLabel(company),
    tone: VEHICLE_LOCATION_TONES.loaned,
  };
}

/** Shorthand for the three questions the screens actually ask. */
export const isVehicleAtHome = (vehicle: VehicleLocationProbe): boolean =>
  describeVehicleLocation(vehicle).kind === 'home';

export const isVehicleLoaned = (vehicle: VehicleLocationProbe): boolean =>
  describeVehicleLocation(vehicle).kind === 'loaned';

// ── Transfers ────────────────────────────────────────────────────────

/** What a transfer writes: the holder, and the status that follows from it. */
export interface VehicleTransferPatch {
  assignedCompany: string;
  status: VehicleStatus;
}

/**
 * "החזר לשינוע" — the vehicle comes back to the company and is available again.
 *
 * A vehicle that was out on a mission (`in_use`) is free the moment it is back
 * on the base, so its status follows the move. A vehicle carrying a fault flag
 * keeps it: coming home is not a repair, and silently painting a broken truck
 * green is the one mistake this screen must not make.
 */
export function returnToBasePatch(vehicle: VehicleLocationProbe): VehicleTransferPatch {
  return {
    assignedCompany: HOME_COMPANY,
    status: isFaultStatus(vehicle.status) ? (vehicle.status as VehicleStatus) : 'available',
  };
}

/**
 * "השאלה לפלוגה" — the vehicle is handed to another company.
 *
 * Handing it back to שינוע is a return, so the home company (or a blank) is
 * routed through {@link returnToBasePatch} rather than filed as a loan to
 * ourselves. A fault flag survives the loan for the same reason it survives
 * the return.
 */
export function loanToCompanyPatch(
  vehicle: VehicleLocationProbe,
  company: string | null | undefined,
): VehicleTransferPatch {
  const target = normalizeCompany(company);
  if (isHomeCompany(target)) return returnToBasePatch(vehicle);
  return {
    assignedCompany: target,
    status: isFaultStatus(vehicle.status) ? (vehicle.status as VehicleStatus) : 'available',
  };
}
